import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, User, LogOut, Clock, FileText, Menu } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useAuth } from '../AuthContext';
import { SearchHistoryItem } from '../utils/searchHistory';
import { 
  searchHistoryApi, 
  initializeRealTimeUpdates, 
  addSearchHistoryListener,
  syncLocalStorageWithServer 
} from '../utils/searchHistoryApi';
import { LogoutConfirmation } from './logout-confirmation';

// Spyglass Brand Icon - 1 outlined square, 1 diamond, 1 triangle, 1 filled circle
const SpyglassBrandIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="12" height="12" rx="2" stroke="#4F46E5" strokeWidth="2" fill="none"/> {/* Indigo-600 (Top-Left Outlined Square) */}
    {/* Sky-500 (Top-Right Diamond - Rotated Square) */}
    <rect x="18" y="4" width="12" height="12" rx="2" fill="#0EA5E9" transform="rotate(45 24 10)"/> 
    {/* Blue-500 (Bottom-Left Triangle - Pointing Up) */}
    <path d="M4 30 L16 30 L10 18 Z" fill="#3B82F6"/> 
    <circle cx="24" cy="24" r="6" fill="#A5B4FC"/>                        {/* Indigo-300 (Bottom-Right Filled Circle) */}
  </svg>
);

const navItems = [
  { name: 'Analysis Reports', href: '/reports', icon: BarChart3 },
];

const Header: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load search history on component mount and listen for updates
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await searchHistoryApi.loadSearchHistory();
        
        // Filter to only show items from the last 12 hours
        const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000);
        const recentHistory = history.filter(item => {
          const itemDate = new Date(item.timestamp);
          return itemDate >= twelveHoursAgo;
        });
        
        setSearchHistory(recentHistory);
      } catch (error) {
        console.error('Failed to load search history:', error);
        setSearchHistory([]);
      }
    };

    // Initialize real-time updates and sync
    initializeRealTimeUpdates();
    syncLocalStorageWithServer();
    loadHistory();
    
    // Listen for real-time updates via WebSocket
    const unsubscribe = addSearchHistoryListener((event) => {
      console.log('Real-time search history update:', event.type);
      loadHistory();
      setLastUpdated(new Date());
    });
    
    // Fallback: listen for legacy localStorage events (for offline compatibility)
    const handleSearchHistoryUpdate = () => {
      loadHistory();
      setLastUpdated(new Date());
    };
    
    window.addEventListener('searchHistoryUpdated', handleSearchHistoryUpdate);
    
    return () => {
      unsubscribe();
      window.removeEventListener('searchHistoryUpdated', handleSearchHistoryUpdate);
    };
  }, [auth.isAuthenticated]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowHistoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleHistoryItemClick = (taskId: string) => {
    // Close dropdown immediately
    setShowHistoryDropdown(false);
    // Small delay to ensure smooth transition, then navigate
    setTimeout(() => {
      navigate(`/dashboard/${taskId}`);
    }, 100);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      // For older items, show actual date
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        ...(diffInDays > 365 ? { year: 'numeric' } : {})
      });
    }
  };

  return (
    <header className="w-full py-2 px-4 md:px-6 bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-50 font-sans">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo and Company Name */}
        <div className="flex items-center">
          <Link to="/" className="flex items-center space-x-2">
            <SpyglassBrandIcon /> {/* Use new brand icon */}
            <span className="text-2xl font-bold text-slate-800">SpyGlass <span className="font-light text-sky-600">AI</span></span>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <Button variant="ghost" size="icon">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Right side icons */}
        <div className="flex items-center space-x-4">
          {/* Analysis Reports Navigation */}
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Button
                key={item.name}
                variant="ghost"
                asChild
                className="hidden md:flex items-center gap-2 px-3 py-2 h-auto"
              >
                <Link to={item.href} title={item.name}>
                  <IconComponent className="h-5 w-5" />
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              </Button>
            );
          })}
          
          {/* Search History Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <Button 
              variant="ghost"
              onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
              className="relative hidden md:flex items-center gap-2 px-3 py-2 h-auto"
              title="Analysis History"
            >
              <Clock className="h-5 w-5" />
              <span className="text-sm font-medium">History</span>
              {searchHistory.length > 0 && (
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-indigo-500 text-white text-xs font-medium">
                  {searchHistory.length > 9 ? '9+' : searchHistory.length}
                </span>
              )}
            </Button>
            
            {/* Mobile version - icon only */}
            <Button 
              variant="ghost"
              size="icon"
              onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
              className="relative md:hidden"
              title="Analysis History"
            >
              <Clock className="h-5 w-5" />
              {searchHistory.length > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 rounded-full bg-indigo-500 text-white text-xs font-medium">
                  {searchHistory.length > 9 ? '9+' : searchHistory.length}
                </span>
              )}
            </Button>
            
            {showHistoryDropdown && (
              <div className="absolute top-full right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden md:w-96">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-4 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Clock size={16} className="text-indigo-600" />
                    Analysis History
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {searchHistory.length > 0 
                      ? `${searchHistory.length} item${searchHistory.length !== 1 ? 's' : ''} • Last: ${formatTimestamp(searchHistory[0].timestamp)}`
                      : 'No analyses yet • Will update automatically'
                    }
                  </p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {searchHistory.length > 0 ? (
                    <div>
                      {searchHistory.map((item, index) => (
                        <button
                          key={item.id}
                          onClick={() => handleHistoryItemClick(item.id)}
                          className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-b-0 transition-all duration-200 group"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0 pr-3">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full bg-indigo-400 opacity-60"></div>
                                <p className="text-sm font-medium text-slate-800 truncate group-hover:text-indigo-700">
                                  {item.productName}
                                </p>
                              </div>
                              <p className="text-xs text-slate-500 truncate ml-4 leading-relaxed">
                                {item.objective}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                item.status === 'completed' 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : item.status === 'processing' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {item.status === 'completed' ? '✓ Complete' : 
                                 item.status === 'processing' ? '⏳ Processing' : '✗ Failed'}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatTimestamp(item.timestamp)}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                      <div className="p-3 bg-slate-50 border-t border-slate-100">
                        <Link 
                          to="/reports" 
                          className="flex items-center justify-center w-full px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-lg transition-colors"
                          onClick={() => setShowHistoryDropdown(false)}
                        >
                          <FileText size={16} className="mr-2" />
                          View All Reports
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-500">
                      <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                        <FileText size={28} className="opacity-40" />
                      </div>
                      <p className="text-sm font-medium mb-1">No recent analyses</p>
                      <p className="text-xs opacity-75">Start your first analysis to see history here</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {auth.isAuthenticated ? (
            <>
              {/* Desktop logout with text */}
              <LogoutConfirmation onConfirm={auth.logout}>
                <Button 
                  variant="ghost"
                  className="hover:bg-red-50 hover:text-red-600 hidden md:flex items-center gap-2 px-3 py-2 h-auto" 
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="text-sm font-medium">Logout</span>
                </Button>
              </LogoutConfirmation>
              
              {/* Mobile logout - icon only */}
              <LogoutConfirmation onConfirm={auth.logout}>
                <Button 
                  variant="ghost"
                  size="icon"
                  className="hover:bg-red-50 hover:text-red-600 md:hidden" 
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </LogoutConfirmation>
            </>
          ) : (
            <>
              {/* Desktop login with text */}
              <Button variant="ghost" asChild className="hidden md:flex items-center gap-2 px-3 py-2 h-auto" title="Login">
                <Link to="/login">
                  <User className="h-5 w-5" />
                  <span className="text-sm font-medium">Login</span>
                </Link>
              </Button>
              
              {/* Mobile login - icon only */}
              <Button variant="ghost" size="icon" asChild className="md:hidden" title="Login">
                <Link to="/login">
                  <User className="h-5 w-5" />
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
